import type { RefRankedWar } from './torn-ref';
import { warEndFor, warTargetAt } from './war-room';

/**
 * Every ranked war Torn lists, the way the website's "Ranked wars" page shows it (FF Scouter's Active Ranked Wars,
 * TornStats' ranked wars): going on (with how far each is from its end), starting within 12 hours, starting later, and
 * recently ended. Pure, browser-safe. Torn's rule (war-room.ts): the first faction to lead by the war's target wins; the
 * target drops 1% an hour from 24 hours in.
 */

/** "Upcoming": wars starting within this many hours. */
export const WAR_SOON_HOURS = 12;
/** "Recently ended": wars that ended within this many hours. */
export const WAR_RECENT_HOURS = 48;

export type WarPhase = 'on' | 'soon' | 'later' | 'ended';

export interface WarLine {
  war: RefRankedWar;
  phase: WarPhase;
  /** The side ahead (null: level, or not two sides). */
  leader: RefRankedWar['factions'][number] | null;
  /** How far ahead (absolute). */
  lead: number;
  /** The lead needed to win now (null: no target, or not running). */
  needed: number | null;
  /** lead ÷ needed, 0…1 (null: no target). */
  progress: number | null;
  /** When it ends if the lead stays (unix s; null: level, no target, or not running). */
  endsAt: number | null;
  /** Each side's share of the war's score, 0…1, in the order of war.factions (both 0.5 at 0–0). */
  shares: [number, number] | null;
}

export function warLine(w: RefRankedWar, now: number): WarLine {
  const [x, y] = w.factions;
  const ended = !!w.end && w.end <= now;
  const phase: WarPhase = ended ? 'ended' : w.start > now ? (w.start - now <= WAR_SOON_HOURS * 3600 ? 'soon' : 'later') : 'on';
  const lead = x && y ? Math.abs(x.score - y.score) : 0;
  const leader = x && y && x.score !== y.score ? (x.score > y.score ? x : y) : null;
  const running = phase === 'on';
  const needed = running ? warTargetAt(w, now) : null;
  const total = x && y ? x.score + y.score : 0;
  return {
    war: w,
    phase,
    leader: ended && w.winner ? (w.factions.find((f) => f.id === w.winner) ?? leader) : leader,
    lead,
    needed,
    progress: needed ? Math.min(1, lead / needed) : running && w.target ? 1 : null,
    endsAt: running && lead > 0 ? warEndFor(w, lead, now) : null,
    shares: x && y ? (total > 0 ? [x.score / total, y.score / total] : [0.5, 0.5]) : null,
  };
}

export interface WarGroups {
  /** Going on: closest to ending first. */
  on: WarLine[];
  /** Starting within WAR_SOON_HOURS: soonest first. */
  soon: WarLine[];
  /** Starting later: soonest first. */
  later: WarLine[];
  /** Ended within WAR_RECENT_HOURS: latest first. */
  ended: WarLine[];
}

export function groupWars(wars: readonly RefRankedWar[], now: number): WarGroups {
  const g: WarGroups = { on: [], soon: [], later: [], ended: [] };
  for (const w of wars) {
    const l = warLine(w, now);
    if (l.phase === 'ended' && (w.end ?? 0) < now - WAR_RECENT_HOURS * 3600) continue;
    g[l.phase].push(l);
  }
  g.on.sort((a, b) => (b.progress ?? -1) - (a.progress ?? -1) || (a.endsAt ?? Infinity) - (b.endsAt ?? Infinity) || a.war.start - b.war.start);
  g.soon.sort((a, b) => a.war.start - b.war.start);
  g.later.sort((a, b) => a.war.start - b.war.start);
  g.ended.sort((a, b) => (b.war.end ?? 0) - (a.war.end ?? 0));
  return g;
}

/** Only the wars a faction name (or id) matches; "" keeps all. */
export function filterWars(wars: readonly RefRankedWar[], q: string): RefRankedWar[] {
  const s = q.trim().toLowerCase();
  if (!s) return [...wars];
  return wars.filter((w) => w.factions.some((f) => f.name.toLowerCase().includes(s) || String(f.id) === s));
}

export interface WarStats {
  on: number;
  soon: number;
  later: number;
  ended: number;
  /** Factions in a war going on. */
  factions: number;
  /** The median target of the wars going on (null: none). */
  medianTarget: number | null;
  /** The war going on closest to its end. */
  closest: WarLine | null;
  /** The most one-sided war going on (largest lead), and the closest race (smallest lead, both sides scoring). */
  biggestLead: WarLine | null;
  tightest: WarLine | null;
}

export function warStats(g: WarGroups): WarStats {
  const targets = g.on.map((l) => l.war.target).filter((t): t is number => typeof t === 'number' && t > 0).sort((a, b) => a - b);
  const mid = targets.length ? (targets.length % 2 ? targets[(targets.length - 1) / 2]! : Math.round((targets[targets.length / 2 - 1]! + targets[targets.length / 2]!) / 2)) : null;
  const scoring = g.on.filter((l) => l.shares && l.war.factions.every((f) => f.score > 0));
  return {
    on: g.on.length,
    soon: g.soon.length,
    later: g.later.length,
    ended: g.ended.length,
    factions: new Set(g.on.flatMap((l) => l.war.factions.map((f) => f.id))).size,
    medianTarget: mid,
    closest: g.on.find((l) => l.progress !== null) ?? null,
    biggestLead: g.on.reduce<WarLine | null>((best, l) => (!best || l.lead > best.lead ? l : best), null),
    tightest: scoring.reduce<WarLine | null>((best, l) => (!best || l.lead < best.lead ? l : best), null),
  };
}

/** Torn's own pages for a war (links only — Omni-Torn never opens them itself). */
export const tornWarReportUrl = (warId: number): string => `https://www.torn.com/war.php?step=rankreport&rankID=${warId}`;
export const tornFactionWarUrl = (factionId: number): string => `https://www.torn.com/factions.php?step=profile&ID=${factionId}#/war/rank`;
